import {
  type CSSProperties,
  type JSX,
  type MouseEvent,
  useEffect,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@workspace/ui/lib/utils';

import { company } from '~/company/Company';
import { mainStore, userStore } from '~/stores';

import { IconButton } from '../isolated/IconButton';
import {
  SHEET_FOOTER_HEIGHT,
  SHEET_HEADER_HEIGHT,
  SHEET_HEIGHT,
} from './constants';
import type { MobileActionDialogProps } from './interfaces';

const BODY_LOCK_CLASS = 'overflow-hidden!';

const MobileActionDialog = (
  props: MobileActionDialogProps,
): JSX.Element | null => {
  const {
    open,
    title,
    footer,
    headerHeight = SHEET_HEADER_HEIGHT,
    contentHeight = SHEET_HEIGHT,
    footerHeight = footer ? SHEET_FOOTER_HEIGHT : '0px',
    className,
    onClose,
    children,
  } = props;

  const cardRef = useRef<HTMLDivElement | null>(null);

  const handleBackdropClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!cardRef.current?.contains(target)) {
      onClose?.(e);
    }
  };

  const sheetStyle = {
    '--sheet-height': contentHeight,
    '--header-height': headerHeight,
    '--footer-height': footerHeight,
  } as CSSProperties;

  const hasAppManagedInsets = company.match({
    mcosmetic: true,
    default: false,
  });

  const cardHeight = hasAppManagedInsets
    ? 'calc(var(--sheet-height) - var(--safe-area-inset-top-fallback))'
    : 'var(--sheet-height)';

  const contentMaxHeight = hasAppManagedInsets
    ? 'calc(var(--sheet-height) - var(--safe-area-inset-top-fallback) - var(--header-height) - var(--footer-height) - env(safe-area-inset-bottom))'
    : 'calc(var(--sheet-height) - var(--header-height) - var(--footer-height))';

  const content = (
    <section
      className={cn(
        'fixed top-0 bottom-0 left-1/2 h-full w-full -translate-x-1/2',
        'z-[1000] self-end',
        mainStore.isRN && 'rounded-t-[32px]',
        'pt-[var(--offset-top)] pb-[var(--offset-bottom)]',
        'backdrop-blur-[10px]',
      )}
      style={{
        ...sheetStyle,
        background:
          'linear-gradient(to bottom, rgba(0,0,0,0.6) 25%, rgba(0,0,0,0.5) 45%, rgba(0,0,0,0.45) 65%, rgba(0,0,0,0.3) 80%, rgba(0,0,0,0.1) 100%)',
      }}
      onClick={handleBackdropClick}
      data-company={company.name}
      dir={userStore.dir}
    >
      <div
        ref={cardRef}
        className="absolute bottom-0 left-0 w-full animate-sheet-appear overflow-hidden"
        style={{ [footer ? 'height' : 'maxHeight']: cardHeight }}
      >
        <div
          className={cn(
            'flex flex-col overflow-hidden rounded-t-[32px] bg-white',
            hasAppManagedInsets && 'pb-safe',
            footer && 'h-full',
          )}
        >
          <div className="flex flex-row flex-nowrap items-center justify-between py-5 pr-4 pl-6">
            {title && <div>{title}</div>}
            {onClose && (
              <IconButton
                size="S"
                variant="outline"
                icon="close"
                onClick={onClose}
              />
            )}
          </div>
          <div
            className={cn(
              'overflow-y-auto overflow-x-hidden',
              footer ? 'min-h-0 flex-1' : undefined,
              className,
            )}
            style={footer ? undefined : { maxHeight: contentMaxHeight }}
          >
            {children}
          </div>
          {footer && <div className="flex">{footer}</div>}
        </div>
      </div>
    </section>
  );

  useEffect(() => {
    if (open && !document.body.classList.contains(BODY_LOCK_CLASS)) {
      document.body.classList.add(BODY_LOCK_CLASS);
    }
    if (!open && document.body.classList.contains(BODY_LOCK_CLASS)) {
      document.body.classList.remove(BODY_LOCK_CLASS);
    }
  }, [open]);

  if (!open) {
    return null;
  }

  return createPortal(content, document.body);
};

export default MobileActionDialog;
